'use client'

import { FEDWATCH_SYMBOL } from '@/service/fedwatch.types'
import { createStreamHook } from '@/service/ws/use-web-socket-with-proto'
import type { AnalyticsStream, FedwatchProbabilityList } from '@/types/generated/public_api'

/**
 * The `analyticsStream` listener key is shared with every Amberdata subscriber, so
 * this hook receives their pushes and they receive ours. Re-filter on the way in —
 * same reasoning as `filterListByPayload` in use-amberdata-analytics-stream.ts.
 *
 * Note the payload's `symbol` is compared against the point's `currency`: FedWatch
 * carries the futures root ("ZQ") in the slot the other analytics use for a
 * currency. It falls back to ZQ because the *pull* sends an empty symbol (which the
 * server expands), and comparing `currency` against '' would match nothing.
 */
function filterFedwatchList(
  list: FedwatchProbabilityList | undefined,
  payload: AnalyticsStream
): FedwatchProbabilityList | undefined {
  if (!list?.items?.length) return undefined

  const root = (payload.symbol || FEDWATCH_SYMBOL).trim().toUpperCase()
  const items = list.items.filter(
    (item) => item.currency?.toUpperCase() === root && item.exchange === payload.exchange
  )

  // `createStreamHook` drops a falsy response, so an all-filtered push never
  // reaches the consumer as an empty grid.
  if (items.length === 0) return undefined
  return { ...list, items }
}

export const useFedwatchStream = createStreamHook<AnalyticsStream, FedwatchProbabilityList>({
  channelKey: 'analyticsStream',
  listenerChannelKey: 'analyticsStream',
  extractResponse: (message, payload) =>
    filterFedwatchList(message.data?.fedwatchProbabilities, payload),
  getPayloadDeps: (p) => [p.exchange, p.symbol, p.analyticsType],
})
